import { store, store$ } from '@redux/store';
import uiStateSlice from 'layout/redux/uiStateSlice';
import {
  fromEvent,
  filter,
  map,
  distinctUntilChanged,
  distinctUntilKeyChanged
} from 'rxjs';
import openLeadModal from 'features/searchResultsExtendedCard';
import { createMarkerHashedKey, isMapPinOnViewport } from 'libraries/mapLibrary';
import LoadingStatus from 'enums/loadingStatus';
import fastdom from 'fastdom';
import housingCardConstructor from '../services/getHousingCard';
import { selectSelectedMarker } from '../redux/selectors';

require('features/leadGenerators');
require('../analytics/leads');

const listViewContainer = document.querySelector('[data-list-view-element]');
const searchResultsContainer = document.querySelector(
  '[data-search-results-content]'
);
const containerClick$ = fromEvent(searchResultsContainer, 'click');

const isStandardCard = event => !!event.target.closest('[data-standard-card]');
const isAnchorElement = event => event.target instanceof HTMLAnchorElement;
const isFavoriteIcon = event =>
  !!event.target.closest('[data-favorite-trigger]');
const isLeadOpener = event =>
  !!event.target.closest('[data-lead-generator-opener]');
const isInteractiveSitePlan = event =>
  !!event.target.closest('[data-interactive-plan-button]');

const cardClick$ = containerClick$.pipe(
  filter(isStandardCard),
  filter(event => !isFavoriteIcon(event)),
  filter(event => !isAnchorElement(event)),
  filter(event => !isLeadOpener(event)),
  filter(event => !isInteractiveSitePlan(event))
);

const leadOpenerClick$ = containerClick$.pipe(
  filter(event => isLeadOpener(event)),
  map(event => event.target.closest('[data-card]'))
);

/**
 * By using event delegation, a listener monitors mouseover events on the standard cards.
 * When a standard card receives the mouseover event, it will store information like
 * the listing id and type od the point of interest that card hovered represents.
 * When mouseover event is not happening on a standard card, then we remove any previous point of
 * interest values from the store.
 */
function onStandardCardHovered(card) {
  if (card) {
    const cardLatitude = card.getAttribute('data-latitude');
    const cardLongitude = card.getAttribute('data-longitude');
    const geographicKey = createMarkerHashedKey(cardLatitude, cardLongitude);
    const shouldDisplayCardOnMap = isMapPinOnViewport(cardLatitude, cardLongitude);

    if (shouldDisplayCardOnMap) {
      store.dispatch(
        uiStateSlice.actions.focusedListingInformation({
          id: card.getAttribute('data-id'),
          communityId: card.getAttribute('data-community-id'),
          geographicKey
          }));
    }
    return;
  }

  store.dispatch(
    uiStateSlice.actions.focusedListingInformation({
      id: null,
      communityId: null,
      geographicKey: null
    })
  );
}

function onSelectedMarkerChange(id){
  const cardPreviousSelected = listViewContainer.querySelector(`[data-standard-card].selectedCard`);
  cardPreviousSelected?.classList?.remove("selectedCard");

  const card = listViewContainer.querySelector(`[data-standard-card][data-id='${id}']`);

  fastdom.mutate(() => {
        card?.classList?.add("selectedCard");
        card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });  
}

/**
 * Redirects the click on the SRP card to the detial page,
 * and removes the tooltip on the map once the URL is opened.
 */
const redirectClickEventToUrl = url => {
  if (!url) {
    return;
  }

  window.open(url, '_blank');
  onStandardCardHovered(null);
};

cardClick$.subscribe(event => {
  const standardCardElement = event.target.closest('[data-standard-card]');
  if (!standardCardElement) {
    return;
  }
  const url =
    event.target?.dataset?.url ?? standardCardElement.querySelector('a')?.href;
  const cardInformation = standardCardElement.dataset;
  const googleAnalyticsDTO = housingCardConstructor(
    cardInformation.type,
    url,
    cardInformation
  ).getCardGoogleAnalyticsEvent();

  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));
  redirectClickEventToUrl(url);
});

leadOpenerClick$.subscribe(leadOpener =>
  openLeadModal(leadOpener.dataset.id, leadOpener.dataset.type)
);

/**
 * The fromEvent observable detects mouseover events on the parent container of the standard cards.
 * By using the distinctUntilChanged pipe operator, we prevent an overflow of signals to the subscriber,
 * ensuring that only a single signal is emitted when a card is hovered or when it stops being hovered.
 */
fromEvent(listViewContainer, 'mouseover')
  .pipe(map(event => event.target?.closest('[data-standard-card]')),  distinctUntilChanged())
  .subscribe(onStandardCardHovered);

  /*
 * Observable to listen when the selectedMarker,
 * to raise the SelectedMarkedChanged
 * Conditions
 * 1. When fetchResultListingsStatus change
 * 2. When selected marker change
 * 3. When fetchStatus is Succeeded or Idle
 * 4. The Page is the same of the last selected marker
 */
store$
  .pipe(
    map(selectSelectedMarker),
    filter(Boolean),
    distinctUntilKeyChanged("marker"),
    filter(({fetchResultListingsStatus}) =>
      fetchResultListingsStatus?.name === LoadingStatus.Succeeded.toString() ||
      fetchResultListingsStatus?.name === LoadingStatus.Idle.toString()),
    filter(({page, marker}) => page === marker?.pageNumber),
    map(({marker}) => marker?.id),
    filter(Boolean))
  .subscribe(onSelectedMarkerChange);

/*
 * Observable to listen when the selectedMarker,
 * to raise the SelectedMarkedChanged
 * Conditions
 * 1. When fetchResultListingsStatus change
 * 2. When selected marker change
 * 3. When fetchStatus is Succeeded or Idle
 * 4. When the page is diferent of the last selected marker 
 */
store$
  .pipe(
    map(selectSelectedMarker),
    filter(Boolean),
    distinctUntilKeyChanged("fetchResultListingsStatus"),
    filter(({fetchResultListingsStatus}) => 
      fetchResultListingsStatus?.name === LoadingStatus.Succeeded.toString() ||
      fetchResultListingsStatus?.name === LoadingStatus.Idle.toString()),
    map(({marker}) => marker?.id),
    filter(Boolean),
  distinctUntilChanged())
  .subscribe(onSelectedMarkerChange);